// services/newsSocketService.js
import { io } from 'socket.io-client';

const REACT_APP_WS_NEWS_USA = `${process.env.REACT_APP_PRIMARY_SERVER_DOMAIN}:${process.env.REACT_APP_PRIMARY_SERVER_PORT}/news`
const REACT_APP_WS_NEWS_IN = `${process.env.REACT_APP_SECONDARY_SERVER_DOMAIN}:${process.env.REACT_APP_SECONDARY_SERVER_PORT}/news`
console.log("📡 News WS US:", REACT_APP_WS_NEWS_USA)
console.log("📡 News WS IN:", REACT_APP_WS_NEWS_IN)

/**
 * Determine whether we should avoid connecting to the 'in' (Indian) server.
 * Returns true if the current hostname is one of the internal hosts (122.160.157.99 or dev.tezkit.com).
 * On those hosts, we do NOT want any connection to the IN server.
 */
const shouldAvoidInServer = (() => {
  const hostname = window.location.hostname;
  const isInternalHost = hostname === "122.160.157.99" || hostname === "dev.tezkit.com";
  return isInternalHost;   // avoid when hostname is internal (matching useChatBundle logic)
})();

console.log(`🚫 NewsSocket – Avoid IN server connections: ${shouldAvoidInServer}`);

class NewsSocketService {
  constructor() {
    const hasUserInfo = JSON.parse(localStorage.getItem("meData"));
    console.log("👤 User info:", hasUserInfo)

    this.sockets = {
      in: null,
      us: null
    };
    this.listeners = new Map();
    this.tenantId = hasUserInfo?.id;
    this.isReady = {
      in: false,
      us: false
    };
    this.hasSubscribed = {
      in: false,
      us: false
    };
  }

  connect() {
    // Always connect to US server
    this.connectToServer('us', REACT_APP_WS_NEWS_USA);

    // Connect to IN server ONLY if we are NOT avoiding it
    if (!shouldAvoidInServer) {
      this.connectToServer('in', REACT_APP_WS_NEWS_IN);
    } else {
      console.warn("⚠️ Avoiding connection to IN server (hostname condition)");
    }

    return this.sockets;
  }

  connectToServer(region, serverUrl) {
    // If region is 'in' and we should avoid it, never create the socket
    if (region === 'in' && shouldAvoidInServer) {
      console.warn(`🚫 [IN] Socket creation blocked by hostname condition`);
      return null;
    }

    if (this.sockets[region]) return this.sockets[region];

    const protocol = window.location.protocol === "https:" ? "https" : "http";

    this.sockets[region] = io(`${protocol}://${serverUrl}`, {
      path: '/socket.io',
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    this.sockets[region].on('connect', () => {
      console.log(`📰 Connected to ${region.toUpperCase()} notification service`);
      this.isReady[region] = true;

      if (!this.hasSubscribed[region] && this.tenantId) {
        const room = `notification_for__${this.tenantId}`;
        console.log(`📝 [${region.toUpperCase()}] Joining room: ${room}`);
        this.sockets[region].emit('subscribe', { room: room });
        this.hasSubscribed[region] = true;
      }
    });

    this.sockets[region].on('training_completion', (data) => {
      console.log(`🎓 [${region.toUpperCase()}] Training completion RECEIVED:`, data);
      const enrichedData = { ...data, _region: region };
      const callbacks = this.listeners.get('training_completion') || [];
      callbacks.forEach(cb => cb(enrichedData));
    });

    this.sockets[region].on('subscribed', (data) => {
      console.log(`✅ [${region.toUpperCase()}] Subscribed to room:`, data);
    });

    this.sockets[region].on('connect_error', (err) => {
      console.error(`❌ [${region.toUpperCase()}] Connection error:`, err.message);
    });

    this.sockets[region].on('disconnect', (reason) => {
      console.log(`📰 [${region.toUpperCase()}] Disconnected from notification service:`, reason);
      this.isReady[region] = false;
      this.hasSubscribed[region] = false;
    });

    return this.sockets[region];
  }

  subscribe(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    console.log(`👂 Listener added for ${event}. Total listeners: ${this.listeners.get(event).length}`);
    return () => this.unsubscribe(event, callback);
  }

  unsubscribe(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event).filter(cb => cb !== callback);
      this.listeners.set(event, callbacks);
      console.log(`👋 Listener removed for ${event}. Remaining: ${callbacks.length}`);
    }
  }

  hasListeners(event) {
    return this.listeners.has(event) && this.listeners.get(event).length > 0;
  }

  isRegionConnected(region) {
    return this.isReady[region] && this.sockets[region]?.connected;
  }

  getConnectionStatus() {
    return {
      in: this.isRegionConnected('in'),
      us: this.isRegionConnected('us')
    };
  }

  disconnect() {
    Object.keys(this.sockets).forEach(region => {
      if (this.sockets[region]) {
        this.sockets[region].disconnect();
        this.sockets[region] = null;
        this.isReady[region] = false;
        this.hasSubscribed[region] = false;
      }
    });
    this.listeners.clear();
    console.log('🔌 Disconnected from all notification services');
  }

  reconnectRegion(region) {
    if (region === 'in' && shouldAvoidInServer) {
      console.warn(`🚫 Cannot reconnect to IN server – blocked by hostname condition`);
      return;
    }

    if (region === 'us') {
      this.connectToServer('us', REACT_APP_WS_NEWS_USA);
    } else if (region === 'in') {
      this.connectToServer('in', REACT_APP_WS_NEWS_IN);
    }
  }
}

export default new NewsSocketService();